import type { EvaluationInput, QuickEvaluationInput } from '../types/cultivar';
import cultivarService from './cultivar';

// Offline queue for cultivar evaluations.
//
// Orchards are exactly where signal isn't. An evaluation represents real work —
// a person walked a block and recorded twenty readings — so losing one to a
// dead zone is unacceptable in a way a dropped GPS waypoint isn't. Everything
// is persisted to IndexedDB *before* the first network attempt, and stays there
// until the server has confirmed both the session and every photo.
//
// Structured deliberately unlike waypointQueue in one respect: sync is
// two-phase. The evaluation record must land first to get a server id, then
// each photo uploads against it. A record therefore has a partially-synced
// state, and `serverEvaluationId` / `uploadedPhotoUuids` checkpoint that
// progress so a retry resumes rather than restarts.

const DB_NAME = 'factree-evaluations';
const DB_VERSION = 1;
const STORE_NAME = 'pending';

export interface QueuedPhoto {
  /** Idempotency key — the server dedupes on this, so a retry is a no-op. */
  clientUuid: string;
  blob: Blob;
  fileName: string;
  caption: string | null;
  latitude: number | null;
  longitude: number | null;
  takenAt: string | null;
}

interface QueueRecord {
  id?: number;
  cultivarId: number;
  cultivarName: string;
  /** A full evaluation of a registered cultivar; null for a quick one. */
  payload: EvaluationInput | null;
  /** A quick evaluation of a cultivar not in the registry (cultivarId 0). */
  quick?: QuickEvaluationInput;
  photos: QueuedPhoto[];
  /** Set once the session itself has been accepted. */
  serverEvaluationId: number | null;
  /** Photos already confirmed by the server, by clientUuid. */
  uploadedPhotoUuids: string[];
  createdAt: string;
  attempts: number;
}

/** Public view of a queued record — no Blobs, safe to hold in React state. */
export interface PendingEvaluation {
  id: number;
  cultivarId: number;
  cultivarName: string;
  evaluatedOn: string;
  valueCount: number;
  photoCount: number;
  photosUploaded: number;
  createdAt: string;
  attempts: number;
  /** A quick evaluation of a cultivar not in the registry. */
  quick: boolean;
}

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('cultivarId', 'cultivarId', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
}

export function newUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older WebViews — the server only requires a well-formed v4.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

const MIN_SYNC_MS = 15_000;
const MAX_SYNC_MS = 300_000;
const BACKOFF_FACTOR = 1.6;

export interface EvalQueueStatus {
  pending: number;
  syncing: boolean;
  lastError: { status?: number; message: string; at: number } | null;
  lastSuccessAt: number | null;
  consecutiveFailures: number;
}

type StatusListener = (s: EvalQueueStatus) => void;

class EvaluationQueue {
  private db: IDBDatabase | null = null;
  private flushing = false;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private syncIntervalMs = MIN_SYNC_MS;
  private listenersAttached = false;
  private pausedFor401 = false;

  private status: EvalQueueStatus = {
    pending: 0,
    syncing: false,
    lastError: null,
    lastSuccessAt: null,
    consecutiveFailures: 0,
  };
  private statusListeners = new Set<StatusListener>();

  getStatus(): EvalQueueStatus {
    return this.status;
  }

  subscribeStatus(cb: StatusListener): () => void {
    this.statusListeners.add(cb);
    // Emit immediately so a freshly-mounted component doesn't render a stale
    // zero before the next flush.
    void this.refreshPendingCount();
    return () => { this.statusListeners.delete(cb); };
  }

  private setStatus(partial: Partial<EvalQueueStatus>): void {
    this.status = { ...this.status, ...partial };
    for (const l of this.statusListeners) {
      try { l(this.status); } catch (e) { console.error('[EvaluationQueue] status listener error', e); }
    }
  }

  private async getDb(): Promise<IDBDatabase> {
    if (!this.db) this.db = await openDb();
    return this.db;
  }

  /**
   * Persist an evaluation and attempt to send it immediately.
   *
   * Returns as soon as the record is safely in IndexedDB — the caller should
   * NOT wait for the network. This is what makes the form feel instant in the
   * field and survive a dropout mid-submit.
   */
  async enqueue(cultivarId: number, cultivarName: string, payload: EvaluationInput, photos: QueuedPhoto[]): Promise<void> {
    const db = await this.getDb();
    const record: QueueRecord = {
      cultivarId,
      cultivarName,
      payload,
      photos,
      serverEvaluationId: null,
      uploadedPhotoUuids: [],
      createdAt: new Date().toISOString(),
      attempts: 0,
    };
    await new Promise<void>((resolve, reject) => {
      const req = tx(db, 'readwrite').add(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    await this.refreshPendingCount();
    if (!this.pausedFor401) void this.flush();
  }

  /**
   * Persist a quick evaluation of a cultivar not in the registry. Same queue,
   * same two phases; the session goes to /cultivar/quick-evaluations.
   */
  async enqueueQuick(payload: QuickEvaluationInput, photos: QueuedPhoto[]): Promise<void> {
    const db = await this.getDb();
    const record: QueueRecord = {
      cultivarId: 0,
      cultivarName: payload.name_seen,
      payload: null,
      quick: payload,
      photos,
      serverEvaluationId: null,
      uploadedPhotoUuids: [],
      createdAt: new Date().toISOString(),
      attempts: 0,
    };
    await new Promise<void>((resolve, reject) => {
      const req = tx(db, 'readwrite').add(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    await this.refreshPendingCount();
    if (!this.pausedFor401) void this.flush();
  }

  /** Records still waiting to sync, without their Blobs. */
  async listPending(cultivarId?: number): Promise<PendingEvaluation[]> {
    const db = await this.getDb();
    const rows = await this.readAll(db);
    return rows
      .filter((r) => cultivarId == null || r.cultivarId === cultivarId)
      .map((r) => ({
        id: r.id!,
        cultivarId: r.cultivarId,
        cultivarName: r.cultivarName,
        evaluatedOn: r.payload?.evaluated_on ?? r.quick?.evaluated_on ?? '',
        valueCount: r.payload?.values?.length ?? 0,
        photoCount: r.photos.length,
        photosUploaded: r.uploadedPhotoUuids.length,
        createdAt: r.createdAt,
        attempts: r.attempts,
        quick: !!r.quick,
      }));
  }

  async pendingCount(): Promise<number> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const req = tx(db, 'readonly').count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  /** Discard a queued evaluation the user has given up on. */
  async discard(recordId: number): Promise<void> {
    const db = await this.getDb();
    await this.deleteRecord(db, recordId);
    await this.refreshPendingCount();
  }

  /**
   * Push queued evaluations to the server.
   *
   * Each record is independent: a record that fails does not block the ones
   * behind it. That matters more here than for waypoints, because a single
   * rejected evaluation (say, an attribute definition deactivated since it was
   * captured) would otherwise strand every later visit indefinitely.
   */
  async flush(): Promise<number> {
    if (this.flushing) return 0;
    if (this.pausedFor401) return 0;
    this.flushing = true;
    this.setStatus({ syncing: true });

    let synced = 0;
    try {
      const db = await this.getDb();
      const records = await this.readAll(db);

      for (const record of records) {
        try {
          await this.syncRecord(db, record);
          await this.deleteRecord(db, record.id!);
          synced += 1;
          this.setStatus({ lastError: null, lastSuccessAt: Date.now(), consecutiveFailures: 0 });
        } catch (e) {
          const err = e as { status?: number; response?: { status?: number; data?: { error?: { message?: string } } }; message?: string };
          const status = err.status ?? err.response?.status;
          const message = err.response?.data?.error?.message || err.message || 'Unknown network error';

          record.attempts += 1;
          await this.putRecord(db, record);
          this.setStatus({
            lastError: { status, message, at: Date.now() },
            consecutiveFailures: this.status.consecutiveFailures + 1,
          });

          if (status === 401) {
            // Auth expired; the axios refresh interceptor already tried and
            // failed. Records stay put and upload once the user signs back in.
            this.pausedFor401 = true;
            this.stopAutoSync();
            console.warn('[EvaluationQueue] paused on 401 — sign-in required before more uploads');
            break;
          }

          // A 4xx that isn't auth means the server will never accept this
          // payload — retrying forever is pointless noise. Keep the record so
          // the user can see it and decide, but stop counting it as an error.
          if (status != null && status >= 400 && status < 500 && status !== 408 && status !== 429) {
            console.warn(`[EvaluationQueue] record ${record.id} rejected (${status}): ${message} — retained for manual review`);
          }
        }
      }

      await this.refreshPendingCount();
      return synced;
    } finally {
      this.flushing = false;
      this.setStatus({ syncing: false });
    }
  }

  /**
   * Two-phase sync for one record, resumable at either phase.
   *
   * Both phases are idempotent server-side via client_uuid, so re-running a
   * partially-completed record is safe even if our checkpoint was lost.
   */
  private async syncRecord(db: IDBDatabase, record: QueueRecord): Promise<void> {
    // Phase 1 — the session itself.
    if (record.serverEvaluationId == null) {
      const res = record.quick
        ? await cultivarService.createQuickEvaluation(record.quick)
        : await cultivarService.createEvaluation(record.cultivarId, record.payload!);
      if (!res.success || !res.data) {
        throw new Error('Server rejected the evaluation');
      }
      record.serverEvaluationId = res.data.id;
      // Checkpoint before touching photos — if a photo upload dies, the retry
      // must not create a second evaluation.
      await this.putRecord(db, record);
    }

    // Phase 2 — photos, one at a time so a large batch on a weak connection
    // makes partial progress rather than all-or-nothing.
    for (let i = 0; i < record.photos.length; i++) {
      const photo = record.photos[i];
      if (record.uploadedPhotoUuids.includes(photo.clientUuid)) continue;

      const upload = record.quick
        ? cultivarService.uploadQuickEvaluationPhoto.bind(cultivarService)
        : cultivarService.uploadEvaluationPhoto.bind(cultivarService);
      await upload(record.serverEvaluationId, photo.blob, {
        client_uuid: photo.clientUuid,
        caption: photo.caption,
        latitude: photo.latitude,
        longitude: photo.longitude,
        taken_at: photo.takenAt,
        sort_order: i,
        file_name: photo.fileName,
      });

      record.uploadedPhotoUuids.push(photo.clientUuid);
      await this.putRecord(db, record);
    }
  }

  startAutoSync(): void {
    if (this.listenersAttached) return;
    this.listenersAttached = true;
    this.pausedFor401 = false;

    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('online', this.handleOnline);

    this.syncIntervalMs = MIN_SYNC_MS;
    this.scheduleNextSync();
    void this.refreshPendingCount();
  }

  stopAutoSync(): void {
    this.listenersAttached = false;
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('online', this.handleOnline);
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
  }

  private scheduleNextSync(): void {
    this.syncTimer = setTimeout(async () => {
      try {
        const count = await this.pendingCount();
        if (count > 0) {
          const synced = await this.flush();
          this.syncIntervalMs = synced > 0
            ? MIN_SYNC_MS
            : Math.min(Math.round(this.syncIntervalMs * BACKOFF_FACTOR), MAX_SYNC_MS);
        } else {
          // Nothing queued — idle right back to the cap rather than waking up
          // every 15 s for no reason.
          this.syncIntervalMs = MAX_SYNC_MS;
        }
      } catch {
        this.syncIntervalMs = Math.min(Math.round(this.syncIntervalMs * BACKOFF_FACTOR), MAX_SYNC_MS);
      } finally {
        if (this.listenersAttached) this.scheduleNextSync();
      }
    }, this.syncIntervalMs);
  }

  private bumpSyncNow(): void {
    if (this.pausedFor401) return;
    this.syncIntervalMs = MIN_SYNC_MS;
    if (this.syncTimer != null) {
      clearTimeout(this.syncTimer);
      this.scheduleNextSync();
    }
  }

  private handleVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      this.bumpSyncNow();
      void this.flush();
    }
  };

  private handleOnline = (): void => {
    this.bumpSyncNow();
    void this.flush();
  };

  // -------------------------------------------------------------------------
  // Internal store helpers
  // -------------------------------------------------------------------------

  private async refreshPendingCount(): Promise<void> {
    try {
      const pending = await this.pendingCount();
      this.setStatus({ pending });
    } catch {
      /* count is cosmetic — never let it break a sync */
    }
  }

  private readAll(db: IDBDatabase): Promise<QueueRecord[]> {
    return new Promise((resolve, reject) => {
      const results: QueueRecord[] = [];
      const req = tx(db, 'readonly').openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          results.push(cursor.value as QueueRecord);
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  private putRecord(db: IDBDatabase, record: QueueRecord): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = tx(db, 'readwrite').put(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private deleteRecord(db: IDBDatabase, id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = tx(db, 'readwrite').delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
}

const evaluationQueue = new EvaluationQueue();
export default evaluationQueue;
